import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PoBreadcrumb,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoTableColumn,
} from '@po-ui/ng-components';
import * as XLSX from 'xlsx';
import { InventarioItem } from '../interface/inventario';
import { InventarioService } from '../service/inventario.service';

@Component({
  selector: 'app-inventario-novo',
  templateUrl: './inventario-novo.component.html',
  styleUrls: ['./inventario-novo.component.css'],
})
export class InventarioNovoComponent implements OnInit {

  codigoInventario: string = '';
  lotes: Array<InventarioItem> = [];
  modoEdicao: boolean = false;
  modoVisualizacao: boolean = false;

  constructor(
    private inventarioService: InventarioService,
    private route: ActivatedRoute,
    private router: Router,
    private poNotificationService: PoNotificationService,
  ) {}

  @ViewChild('qtdModal', { static: true }) qtdModal!: PoModalComponent;

  ngOnInit(): void {
    this.modoVisualizacao = this.route.snapshot.data['modo'] === 'visualizar';
    this.route.params.subscribe((params) => {
      if ('CZ08COD' in params) {
        this.codigoInventario = params['CZ08COD'];
        this.modoEdicao = !this.modoVisualizacao;
        this.inventarioService.getInventario(this.codigoInventario).subscribe((inv) => {
          this.lotes = inv.items.map((item, index) => ({ ...item, contador: index + 1 }));
        });
      }
    });
  }

  get tituloPagina(): string {
    if (this.modoVisualizacao) {
      return 'Visualizar Inventário';
    }

    return this.modoEdicao ? 'Editar Inventário' : 'Novo Inventário';
  }

  get breadcrumb(): PoBreadcrumb {
    return {
      items: [
        { label: 'Novo Inventário', action: () => this.router.navigateByUrl('/estoque/inventario/lista') },
        { label: this.tituloPagina },
      ],
    };
  }

  private readonly colunasImportacao: Array<PoTableColumn> = [
    { property: 'CZ08LOTE', label: 'Lote', width: '15%' },
    { property: 'CB1COD', label: 'Cód. Produto', width: '15%' },
    { property: 'CB1DESC', label: 'Desc. Produto', width: '35%' },
    { property: 'NB8QTDORI', label: 'Qtd Peças', width: '15%', type: 'number' },
    { property: 'NB8VOLUME', label: 'Volume', width: '15%', type: 'number' },
  ];

  private readonly colunasVisualizacao: Array<PoTableColumn> = [
    { property: 'contador', label: 'Nº', width: '5%', type: 'number' },
    { ...this.colunasImportacao[0], width: '15%' },
    { ...this.colunasImportacao[1], width: '12%' },
    { ...this.colunasImportacao[2], width: '25%' },
    { ...this.colunasImportacao[3], width: '10%' },
    { ...this.colunasImportacao[4], width: '10%' },
    { property: 'CZ08AREA', label: 'Área', width: '10%' },
    { property: 'CUSRNOME', label: 'Usuário', width: '10%' },
    {
      property: 'CZ08ESTOQU', label: 'Estoque', width: '10%', type: 'label',
      labels: [
        { value: 'S', color: 'color-10', label: 'Sim' },
        { value: 'C', color: 'color-08', label: 'Conferir' },
        { value: '',  color: 'caption-tag-32', label: 'Não Lido' },
      ],
    },
  ];

  get colunas(): Array<PoTableColumn> {
    return this.modoVisualizacao ? this.colunasVisualizacao : this.colunasImportacao;
  }

  importarPlanilha(event: Event): void {
    if (this.modoVisualizacao) { return; }

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) { return; }

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false, raw: false });

      // le a primeira coluna independente do nome do cabecalho
      const lotesArquivo: string[] = [];
      rows.forEach((row, index) => {
        const valor = row && row[0] != null ? String(row[0]).trim() : '';
        if (!valor) { return; }
        if (index === 0 && ['LOTE', 'LOTES'].includes(valor.toUpperCase())) { return; }
        lotesArquivo.push(valor);
      });

      const lotesExistentes = new Set(this.lotes.map((l) => l.CZ08LOTE));
      const novos = Array.from(new Set(lotesArquivo)).filter((l) => !lotesExistentes.has(l));

      if (novos.length === 0) {
        this.poNotificationService.warning('Nenhum lote novo encontrado no arquivo.');
        return;
      }

      this.inventarioService.consultarLotes({ items: novos }).subscribe((ret) => {
        this.lotes = [...this.lotes, ...ret.items];
        this.poNotificationService.success(`${ret.items.length} lote(s) importado(s).`);
      });
    };

    reader.readAsArrayBuffer(file);
    input.value = '';
  }

  salvar(): void {
    if (this.modoVisualizacao) { return; }

    if (this.lotes.length === 0) {
      this.poNotificationService.warning('Importe ao menos um lote antes de salvar.');
      return;
    }

    this.qtdModal.open();
  }

  confirmarSalvar: PoModalAction = {
    label: 'Confirmar',
    action: () => {
      this.qtdModal.close();
      this.persistir();
    },
  };

  cancelarQuantidade: PoModalAction = {
    label: 'Cancelar',
    danger: true,
    action: () => this.qtdModal.close(),
  };

  private persistir(): void {
    if (this.modoVisualizacao) { return; }

    const payload = { items: this.lotes.map((l) => l.CZ08LOTE) };

    if (this.modoEdicao) {
      this.inventarioService.atualizarInventario(this.codigoInventario, payload).subscribe(() => {
        this.poNotificationService.success('Inventário atualizado com sucesso!');
        this.router.navigateByUrl('/estoque/inventario/lista');
      });
    } else {
      this.inventarioService.criarInventario(payload).subscribe(() => {
        this.poNotificationService.success('Inventário criado com sucesso!');
        this.router.navigateByUrl('/estoque/inventario/lista');
      });
    }
  }

  cancelar(): void {
    this.router.navigateByUrl('/estoque/inventario/lista');
  }
}
